import {
  type UnitPerformanceInsightsResponse,
  type UnitPerformanceLlmInsightResponse,
} from '../../../../api/customEndpoints/fetchUnitPerformanceScores';
import { useMutation, type UseQueryResult } from '@tanstack/react-query';
import { useDataProvider, useNotify, useTranslate } from 'ra-core';
import { type AdminDataProvider } from '../../../../api/adminDataProvider';
import { useForm } from 'react-hook-form';
import { type SetUnitInsightsBody } from '../../../../api/customEndpoints/setUnitInsights';
import { CardContent } from '../../../../components/ui/card';
import { Textarea } from '../../../../components/ui/textarea';
import { Button } from '../../../../components/ui/button';
import { Loader2 } from 'lucide-react';
import { Error as ReactAdminError } from '../../../../components/admin/error';
import { Loading } from '../../../../components/admin/loading';

export function InsightsCard({
  unitLlmInsights,
  unitInsights,
  unit,
}: {
  unitLlmInsights: UseQueryResult<UnitPerformanceLlmInsightResponse>;
  unitInsights: UseQueryResult<UnitPerformanceInsightsResponse>;
  unit: { name: string; id: number };
}) {
  const translate = useTranslate();
  // If we've already got content in the unit insights, there's no need to wait for llm insights. Just let the user edit / publish their existing insights.
  const shouldWaitForLlm = !unitInsights.data?.insights.content;

  return (
    <CardContent>
      <h2 className="text-xl font-semibold">
        {translate('moonstar.unitPerformance.insights.title')}
      </h2>
      <p className="mb-3 text-sm text-muted-foreground">
        {translate('moonstar.unitPerformance.insights.description', {
          unit: unit.name,
        })}
      </p>

      {unitInsights.isPending ||
      (shouldWaitForLlm && unitLlmInsights.isPending) ? (
        <Loading />
      ) : unitInsights.isError ? (
        <ReactAdminError
          error={unitInsights.error}
          resetErrorBoundary={() => {
            void unitInsights.refetch();
          }}
        />
      ) : (
        <InsightsCardContent
          unitId={unit.id}
          llmInsights={unitLlmInsights}
          unitInsights={unitInsights.data.insights}
        />
      )}
    </CardContent>
  );
}

function InsightsCardContent({
  unitId,
  unitInsights,
  llmInsights,
}: {
  unitId: number;
  unitInsights: UnitPerformanceInsightsResponse['insights'];
  llmInsights: UseQueryResult<UnitPerformanceLlmInsightResponse>;
}) {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const insightsForm = useForm({
    defaultValues: {
      content: unitInsights.content ?? llmInsights.data?.content,
      status:
        unitInsights.status === 'published'
          ? ('PUBLISHED' as const)
          : ('DRAFT' as const),
    },
  });

  const notify = useNotify();
  const translate = useTranslate();

  const setInsight = useMutation({
    mutationFn: async (data: {
      content: string | undefined;
      status: 'PUBLISHED' | 'DRAFT';
    }) => {
      if (!data.content) {
        throw new Error(
          translate('moonstar.unitPerformance.insights.emptyContent')
        );
      }

      const body: SetUnitInsightsBody = {
        richContent: {
          version: 1,
          content: data.content,
        },
        status: data.status,
        contentFromLlmInsightId:
          data.content === llmInsights.data?.content
            ? llmInsights.data.id
            : undefined,
      };

      if (unitInsights.id) {
        await dataProvider.putUnitInsights({
          insightId: unitInsights.id,
          unitId,
          body,
        });
      } else {
        await dataProvider.postUnitInsights({
          unitId,
          body,
        });
      }
    },
    onSuccess: (_data, vars) => {
      notify(
        translate(
          vars.status === 'DRAFT'
            ? 'moonstar.unitPerformance.insights.savedAsDraft'
            : 'moonstar.unitPerformance.insights.published'
        ),
        { type: 'success' }
      );
    },
    onError: (error: Error) => {
      notify(
        translate('moonstar.unitPerformance.insights.saveError', {
          error: error.message,
        }),
        { type: 'error' }
      );
    },
  });

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={insightsForm.handleSubmit(
        (data) => setInsight.mutateAsync(data),
        () =>
          notify(translate('moonstar.unitPerformance.insights.saveFailed'), {
            type: 'error',
          })
      )}
    >
      <Textarea
        required
        className="min-h-32"
        {...insightsForm.register('content')}
      />

      <div className="flex justify-between gap-2">
        <Button
          variant="outline"
          disabled={
            setInsight.isPending ||
            unitInsights.status === 'published' ||
            !insightsForm.formState.isDirty
          }
          onClick={() => insightsForm.setValue('status', 'DRAFT')}
          type="submit"
        >
          {setInsight.isPending && setInsight.variables.status === 'DRAFT' ? (
            <Loader2 className="animate-spin" />
          ) : null}
          {translate('moonstar.unitPerformance.insights.saveAsDraft')}
        </Button>

        <Button
          variant="default"
          disabled={
            setInsight.isPending ||
            (!insightsForm.formState.isDirty &&
              unitInsights.status === 'published')
          }
          onClick={() => insightsForm.setValue('status', 'PUBLISHED')}
          type="submit"
        >
          {setInsight.isPending &&
          setInsight.variables.status === 'PUBLISHED' ? (
            <Loader2 className="animate-spin" />
          ) : null}
          {translate('moonstar.unitPerformance.insights.publish')}
        </Button>
      </div>
    </form>
  );
}
